import React, { useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { printMuiDoc } from "./printMuiDoc";

const C = {
  navy: "#1b1f4b",
  green: "#10b981",
  greenDk: "#0b7d57",
  ink: "#1f2433",
  ink2: "#3b4256",
  muted: "#6b7280",
  faint: "#9aa1ad",
  line: "#e3e6ec",
  line2: "#eef0f4",
  band: "#f1f3f9",
  notice: "#fff1e6",
  noticeInk: "#b4441c",
  chip: "#f1f3f6",
  bg: "#e9ebf0",
};

const FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&family=Noto+Sans+Arabic:wght@400;500;700&display=swap";

const money = (v) =>
  (+v || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const has = (v) =>
  v !== null &&
  v !== undefined &&
  String(v).trim() !== "" &&
  String(v).trim() !== "null";

const Ar = ({ children, sx, ...rest }) =>
  has(children) ? (
    <Box
      component="span"
      sx={{
        fontFamily: '"Noto Sans Arabic","Segoe UI",Tahoma,Arial,sans-serif',
        direction: "rtl",
        unicodeBidi: "isolate",
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  ) : null;

/** Keep phones / IBAN / account nos from flipping in RTL Arabic. */
const looksLikePhoneOrCode = (v) =>
  /(?:\+?\d[\d\s\-()]{5,}|IBAN|AE\d{2}|[\d\s]{8,})/i.test(String(v || ""));

const Ltr = ({ children, sx, ...rest }) =>
  has(children) ? (
    <Box
      component="span"
      dir="ltr"
      sx={{
        direction: "ltr",
        unicodeBidi: "isolate",
        display: "inline-block",
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  ) : null;

/** Split Arabic line so trailing phone/number stays LTR (e.g. الهاتف: +971…). */
const ArMixed = ({ children, sx, ...rest }) => {
  const text = String(children ?? "");
  const m = text.match(/^(.*?)([+\d][\d\s\-()+]{5,})\s*$/);
  if (m) {
    return (
      <Ar sx={sx} {...rest}>
        {m[1]}
        <Ltr>{m[2].trim()}</Ltr>
      </Ar>
    );
  }
  if (looksLikePhoneOrCode(text) && !/[\u0600-\u06FF]/.test(text)) {
    return (
      <Ltr sx={{ fontFamily: '"Noto Sans Arabic","Segoe UI",Tahoma,Arial,sans-serif', ...sx }} {...rest}>
        {text}
      </Ltr>
    );
  }
  return (
    <Ar sx={sx} {...rest}>
      {text}
    </Ar>
  );
};

const Dh = ({ sx }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      width: "0.9em",
      height: "0.9em",
      verticalAlign: "-0.08em",
      mr: "0.25em",
      bgcolor: "currentColor",
      WebkitMask:
        'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M6 3h5a9 9 0 010 18H6z\' fill=\'none\' stroke=\'black\' stroke-width=\'2.6\'/%3E%3Cpath d=\'M1.5 9.5h20M1.5 14.5h20\' stroke=\'black\' stroke-width=\'2.2\'/%3E%3C/svg%3E") center / contain no-repeat',
      mask: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M6 3h5a9 9 0 010 18H6z\' fill=\'none\' stroke=\'black\' stroke-width=\'2.6\'/%3E%3Cpath d=\'M1.5 9.5h20M1.5 14.5h20\' stroke=\'black\' stroke-width=\'2.2\'/%3E%3C/svg%3E") center / contain no-repeat',
      ...sx,
    }}
  />
);

function computeRows(doc) {
  const rate = doc.vatRate ?? 5;
  const table = doc.table || { groups: [] };
  let gross = 0;
  let dsc = 0;
  let net = 0;
  let vat = 0;
  let sav = 0;

  const groups = (table.groups || [])
    .filter((g) => g.rows && g.rows.length)
    .map((g) => {
      let sum = 0;
      const rows = g.rows.map((r) => {
        const qty = r.qty ?? 1;
        const price = +r.price || 0;
        const disc = +r.discount || 0;
        const g1 = qty * price;
        const ex = g1 - disc;
        const v =
          r.vat != null
            ? +r.vat
            : +(ex * ((r.rate ?? rate) / 100)).toFixed(2);
        const tot = ex + v;
        const sv = +r.saving || 0;
        gross += g1;
        dsc += disc;
        net += ex;
        vat += v;
        sav += sv;
        sum += tot;
        return { ...r, qty, price, disc, sv, gross: g1, ex, v, tot };
      });
      return { ...g, rows, sum };
    });

  return {
    groups,
    gross,
    dsc,
    net,
    vat,
    sav,
    total: net + vat,
    showSav: !!doc.showSavings,
    discCol: groups.some((g) => g.rows.some((r) => r.disc > 0)),
    rate,
  };
}

const thSx = (right) => ({
  bgcolor: `${C.navy} !important`,
  color: "#fff !important",
  fontWeight: 700,
  fontSize: "10.8px",
  lineHeight: 1.25,
  py: "7px",
  px: "7px",
  borderBottom: 0,
  verticalAlign: "bottom",
  textAlign: right ? "right" : "left",
  whiteSpace: "nowrap",
});

const tdSx = (right) => ({
  py: "5px",
  px: "7px",
  fontSize: "10.8px",
  borderBottom: `1px solid ${C.line2}`,
  verticalAlign: "top",
  textAlign: right ? "right" : "left",
  color: C.ink,
  fontVariantNumeric: "tabular-nums",
});

/**
 * Bayanat delivery-note layout in MUI (matches delivery-note.html).
 */
export default function BayanatDeliveryNoteMui({ doc, hideToolbar = false }) {
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (document.querySelector(`link[href="${FONT_HREF}"]`)) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = FONT_HREF;
    document.head.appendChild(link);
  }, []);

  // Multi-page print: keep every page white (no gray screen bg on page 2+)
  useEffect(() => {
    if (typeof document === "undefined") return;
    const id = "bn-print-white-bg";
    if (document.getElementById(id)) return;
    const style = document.createElement("style");
    style.id = id;
    style.textContent = `
      @media print {
        html, body, #root, #root > div {
          background: #fff !important;
          background-color: #fff !important;
        }
      }
    `;
    document.head.appendChild(style);
  }, []);

  const calc = useMemo(() => (doc ? computeRows(doc) : null), [doc]);
  console.log("cal",calc)

  const autoPrint =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("autoPrint");

  if (!doc || !calc) return null;

  const { groups, gross, dsc, net, vat, sav, total, showSav, discCol, rate } =
    calc;
  const logoSrc = doc.logo || "logo.png";
  const isPayment = doc.kind === "payment";
  const mode = doc.table?.mode || "items";
  const isDn = mode === "dn";
  const isLease = mode === "lease";
  // DN cols: # | Vehicle | Delivery note | [Discount] | Excl.VAT | VAT | [Savings] | Total
  const colCount = isLease
    ? 8 + (discCol ? 1 : 0) /* # plate desc days price [disc] excl vat total */
    : isDn
      ? 6 + (discCol ? 1 : 0) + (showSav ? 1 : 0)
      : 7 + (discCol ? 1 : 0) + (showSav ? 1 : 0);
  const allocatedAmt = (+doc.amount || 0) - (+doc.balance || 0);
  const appliedList = (doc.applied || []).filter(has);

  const handlePrint = () => {
    printMuiDoc({ title: doc.titleEn || "Document" });
  };

  const handleClose = () => {
    try {
      window.close();
    } catch (e) {
      // ignore
    }
    // Fallback when window was not opened by script
    if (!window.closed) {
      try {
        window.history.back();
      } catch (e2) {
        // ignore
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: C.bg,
        py: 2,
        fontFamily: '"Roboto",Arial,"Helvetica Neue",sans-serif',
        color: C.ink,
        fontSize: "11.5px",
        lineHeight: 1.45,
        WebkitPrintColorAdjust: "exact",
        printColorAdjust: "exact",
        "@media print": {
          bgcolor: "#fff !important",
          backgroundColor: "#fff !important",
          backgroundImage: "none !important",
          minHeight: "0 !important",
          height: "auto !important",
          py: 0,
          "& .bn-toolbar": { display: "none !important" },
          "& .bn-page": {
            m: 0,
            width: "auto",
            minHeight: "0 !important",
            height: "auto !important",
            p: "10mm",
            boxShadow: "none",
            bgcolor: "#fff !important",
            backgroundColor: "#fff !important",
          },
        },
      }}
    >
      {!hideToolbar && !autoPrint && (
        <Box
          className="bn-toolbar"
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            width: "210mm",
            maxWidth: "100%",
            mx: "auto",
            mb: 1.5,
            px: 1.5,
            py: 1,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            bgcolor: "#fff",
            borderRadius: 1,
            boxShadow: "0 1px 6px rgba(0,0,0,.08)",
            boxSizing: "border-box",
          }}
        >
          <Button
            variant="contained"
            size="small"
            onClick={handlePrint}
            sx={{
              textTransform: "none",
              bgcolor: C.green,
              "&:hover": { bgcolor: C.greenDk },
            }}
          >
            Print
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleClose}
            sx={{ textTransform: "none", borderColor: C.line, color: C.ink2 }}
          >
            Close
          </Button>
        </Box>
      )}
      <Box
        id="doc"
        className="bn-page"
        sx={{
          width: "210mm",
          minHeight: "297mm",
          mx: "auto",
          bgcolor: "#fff",
          p: "12mm",
          boxShadow: "0 2px 14px rgba(0,0,0,.12)",
          boxSizing: "border-box",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "20px",
            pb: 0.5,
            borderBottom: `4px solid ${C.green}`,
          }}
        >
          <Box
            component="img"
            src={logoSrc}
            alt=""
            sx={{ height: 90, width: "auto" }}
          />
          <Box sx={{ textAlign: "right" }}>
            <Ar sx={{ display: "block", fontSize: 15, fontWeight: 700, color: C.ink2 }}>
              {doc.titleAr}
            </Ar>
            <Typography
              component="h1"
              sx={{
                m: 0,
                fontSize: 25,
                lineHeight: 1.15,
                color: C.navy,
                fontWeight: 700,
                letterSpacing: "0.01em",
              }}
            >
              {doc.titleEn}
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 0.75,
                justifyContent: "flex-end",
                mt: 1,
                flexWrap: "wrap",
              }}
            >
              {(doc.badges || []).map((b, i) => (
                <Chip
                  key={i}
                  size="small"
                  label={
                    <>
                      {b.en}
                      {b.ar ? (
                        <>
                          {" · "}
                          <Ar>{b.ar}</Ar>
                        </>
                      ) : null}
                    </>
                  }
                  sx={{
                    height: "auto",
                    borderRadius: "5px",
                    fontSize: "10.5px",
                    fontWeight: 700,
                    bgcolor:
                      b.cls === "st-paid"
                        ? "#e3f6ec"
                        : b.cls === "st-partial"
                          ? "#fff1dc"
                          : b.cls === "st-unpaid"
                            ? "#fde8e8"
                            : C.chip,
                    color:
                      b.cls === "st-paid"
                        ? C.greenDk
                        : b.cls === "st-partial"
                          ? "#a65f00"
                          : b.cls === "st-unpaid"
                            ? "#c62828"
                            : C.ink2,
                    "& .MuiChip-label": { px: 1.1, py: 0.35 },
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>

        {/* Company bilingual */}
        <Grid container spacing={2} sx={{ py: 1.25 }}>
          <Grid item xs={6}>
            <Typography sx={{ fontWeight: 700, fontSize: "11.5px", color: C.ink }}>
              {doc.company?.name}
            </Typography>
            {(doc.company?.lines || []).map((line, i) => (
              <Typography key={i} sx={{ fontSize: "10.8px", color: C.ink2, lineHeight: 1.5 }}>
                {line}
              </Typography>
            ))}
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ textAlign: "right" }}>
              <Ar sx={{ display: "block", fontWeight: 700, fontSize: "11.5px", color: C.ink }}>
                {doc.company?.nameAr}
              </Ar>
              {(doc.company?.linesAr || []).map((line, i) => (
                <ArMixed
                  key={i}
                  sx={{
                    display: "block",
                    fontSize: "10.8px",
                    color: C.ink2,
                    lineHeight: 1.5,
                  }}
                >
                  {line}
                </ArMixed>
              ))}
            </Box>
          </Grid>
        </Grid>

        {/* Notice */}
        {doc.notice && (
          <Box
            sx={{
              display: "inline-block",
              bgcolor: C.notice,
              color: C.noticeInk,
              fontWeight: 700,
              fontSize: "10.5px",
              px: 1.5,
              py: 0.5,
              borderRadius: "6px",
              mb: 1.5,
            }}
          >
            {doc.notice.en}
            {doc.notice.ar ? (
              <>
                {" / "}
                <Ar>{doc.notice.ar}</Ar>
              </>
            ) : null}
          </Box>
        )}

        {/* Refs */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
            gap: "8px 12px",
            mb: 1.25,
          }}
        >
          {(doc.refs || [])
            .filter((r) => has(r[2]))
            .map((r, i) => (
              <Box key={i}>
                <Typography sx={{ fontSize: "9.5px", color: C.muted, lineHeight: 1.3 }}>
                  {r[0]}
                  {has(r[1]) && (
                    <Ar sx={{ display: "block", fontSize: 9, color: C.faint, textAlign: "left" }}>
                      {r[1]}
                    </Ar>
                  )}
                </Typography>
                <Typography
                  sx={{
                    display: "block",
                    fontSize: "11.5px",
                    fontWeight: 700,
                    mt: 0.25,
                    overflowWrap: "anywhere",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {r[2]}
                </Typography>
              </Box>
            ))}
        </Box>

        {/* Payment amount hero */}
        {isPayment && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1.5,
              mb: 1.5,
              p: 1.5,
              borderRadius: "8px",
              border: `1px solid ${C.line}`,
              bgcolor: doc.direction === "out" ? "#fff7f5" : "#eefbf4",
              borderLeft: `4px solid ${
                doc.direction === "out" ? "#e07a5f" : C.green
              }`,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: "9.5px", color: C.muted }}>
                {doc.amountLabel?.en || "Amount paid"}
                <Ar sx={{ ml: 0.6, fontSize: 9, color: C.faint }}>
                  {doc.amountLabel?.ar || "المبلغ المدفوع"}
                </Ar>
              </Typography>
              <Typography
                sx={{
                  fontSize: 26,
                  fontWeight: 700,
                  color: doc.direction === "out" ? C.navy : C.greenDk,
                  fontVariantNumeric: "tabular-nums",
                  mt: 0.25,
                  whiteSpace: "nowrap",
                }}
              >
                <Dh />
                {money(doc.amount)}
              </Typography>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Typography sx={{ fontSize: "9.5px", color: C.muted }}>
                {doc.partyLabel?.en || "Paid to"}
                <Ar sx={{ display: "block", fontSize: 9, color: C.faint }}>
                  {doc.partyLabel?.ar || "صرف إلى"}
                </Ar>
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 700, mt: 0.25 }}>
                {doc.party?.name || ""}
              </Typography>
            </Box>
            {has(doc.amountWords) && (
              <Typography
                sx={{
                  gridColumn: "1 / -1",
                  fontSize: 11,
                  color: C.ink2,
                  mt: 0.25,
                  borderTop: `1px dashed ${C.line}`,
                  pt: 0.875,
                }}
              >
                Amount in words:{" "}
                <b style={{ fontWeight: 600 }}>{doc.amountWords}</b>
              </Typography>
            )}
          </Box>
        )}

        {/* Boxes — CSS grid (avoid MUI Grid negative-margin overlap on table) */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: "12px",
            mb: 2,
            clear: "both",
            position: "relative",
            zIndex: 0,
          }}
        >
          {(doc.boxes || []).map((b, i) => (
            <Box
              key={i}
              sx={{
                border: `1px solid ${C.line}`,
                borderRadius: "8px",
                px: 1.5,
                py: 1,
                minWidth: 0,
                overflow: "hidden",
                breakInside: "avoid",
                bgcolor: "#fff",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 1,
                  mb: 1,
                }}
              >
                <Typography sx={{ fontSize: 12, color: C.navy, fontWeight: 700, m: 0 }}>
                  {b.en}
                </Typography>
                <Ar sx={{ fontSize: "11.5px", color: C.ink2 }}>{b.ar}</Ar>
              </Box>
              {has(b.name) && (
                <Typography sx={{ fontWeight: 700, fontSize: 12, mb: 0.125 }}>
                  {b.name}
                </Typography>
              )}
              {has(b.nameAr) && (
                <Ar
                  sx={{
                    display: "block",
                    fontSize: 11,
                    color: C.ink2,
                    mb: 0.75,
                    textAlign: "left",
                  }}
                >
                  {b.nameAr}
                </Ar>
              )}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  columnGap: 1.25,
                  rowGap: "1px",
                  fontSize: "10.5px",
                }}
              >
                {(b.rows || [])
                  .filter((r) => has(r[2]))
                  .map((r, ri) => (
                    <React.Fragment key={ri}>
                      <Typography
                        sx={{
                          color: C.muted,
                          whiteSpace: "nowrap",
                          fontSize: "10.5px",
                        }}
                      >
                        {r[0]}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "10.5px",
                          overflowWrap: "anywhere",
                          m: 0,
                        }}
                      >
                        {r[0] === "Phone" ||
                        r[0] === "TRN" ||
                        r[0] === "IBAN" ||
                        r[0] === "Account no." ||
                        looksLikePhoneOrCode(r[2]) ? (
                          <Ltr>{r[2]}</Ltr>
                        ) : (
                          r[2]
                        )}
                        {has(r[3]) && (
                          <ArMixed
                            sx={{
                              display: "block",
                              color: C.ink2,
                              fontSize: "10.5px",
                              textAlign: "left",
                            }}
                          >
                            {r[3]}
                          </ArMixed>
                        )}
                      </Typography>
                    </React.Fragment>
                  ))}
              </Box>
            </Box>
          ))}
        </Box>

        {isPayment ? (
          <>
            <Table
              size="small"
              sx={{
                width: "100%",
                borderCollapse: "collapse",
                mb: 1.5,
                "& .MuiTableCell-root": { borderColor: C.line2 },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx(false)}>
                    {doc.appliedLabel?.en || "Applied to purchase orders"}
                    <Ar
                      sx={{
                        display: "block",
                        fontWeight: 500,
                        fontSize: "9.5px",
                        opacity: 0.85,
                      }}
                    >
                      {doc.appliedLabel?.ar || "أوامر الشراء المسددة"}
                    </Ar>
                  </TableCell>
                  <TableCell sx={thSx(false)}>
                    Adjustment
                    <Ar
                      sx={{
                        display: "block",
                        fontWeight: 500,
                        fontSize: "9.5px",
                        opacity: 0.85,
                      }}
                    >
                      طريقة التسوية
                    </Ar>
                  </TableCell>
                  <TableCell sx={thSx(true)}>
                    Allocated
                    <Ar
                      sx={{
                        display: "block",
                        fontWeight: 500,
                        fontSize: "9.5px",
                        opacity: 0.85,
                        textAlign: "right",
                      }}
                    >
                      المبلغ المخصص
                    </Ar>
                  </TableCell>
                  <TableCell sx={thSx(true)}>
                    Unallocated balance
                    <Ar
                      sx={{
                        display: "block",
                        fontWeight: 500,
                        fontSize: "9.5px",
                        opacity: 0.85,
                        textAlign: "right",
                      }}
                    >
                      الرصيد غير المخصص
                    </Ar>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={tdSx(false)}>
                    {appliedList.length ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {appliedList.map((x, i) => (
                          <Chip
                            key={i}
                            size="small"
                            label={x}
                            sx={{
                              height: 22,
                              fontSize: 10,
                              bgcolor: C.chip,
                              fontWeight: 600,
                            }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography sx={{ color: C.faint, fontSize: "10.5px" }}>
                        Not applied yet
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={tdSx(false)}>{doc.adjustment || "—"}</TableCell>
                  <TableCell sx={{ ...tdSx(true), fontWeight: 700 }}>
                    {money(allocatedAmt)}
                  </TableCell>
                  <TableCell sx={tdSx(true)}>{money(doc.balance)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            {has(doc.remarks) && (
              <Box
                sx={{
                  border: `1px solid ${C.line}`,
                  borderRadius: "8px",
                  px: 1.5,
                  py: 1,
                  mb: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.75,
                  }}
                >
                  <Typography
                    sx={{ fontSize: 12, color: C.navy, fontWeight: 700 }}
                  >
                    Remarks
                  </Typography>
                  <Ar sx={{ fontSize: "11.5px", color: C.ink2 }}>ملاحظات</Ar>
                </Box>
                <Typography sx={{ fontSize: "11px", color: C.ink2 }}>
                  {doc.remarks}
                </Typography>
              </Box>
            )}
          </>
        ) : (
          <>
        {/* Line table */}
        <Table
          size="small"
          sx={{
            width: "100%",
            borderCollapse: "collapse",
            position: "relative",
            zIndex: 1,
            mt: 0.5,
            "& .MuiTableCell-root": { borderColor: C.line2 },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={thSx(false)}>#</TableCell>
              {isLease ? (
                <>
                  <TableCell sx={thSx(false)}>
                    Vehicle
                    <Ar sx={{ display: "block", fontWeight: 500, fontSize: "9.5px", opacity: 0.85 }}>
                      المركبة
                    </Ar>
                  </TableCell>
                  <TableCell sx={thSx(false)}>
                    Description
                    <Ar sx={{ display: "block", fontWeight: 500, fontSize: "9.5px", opacity: 0.85 }}>
                      الوصف
                    </Ar>
                  </TableCell>
                  <TableCell sx={thSx(true)}>
                    Days
                    <Ar sx={{ display: "block", fontWeight: 500, fontSize: "9.5px", opacity: 0.85, textAlign: "right" }}>
                      عدد الأيام
                    </Ar>
                  </TableCell>
                  <TableCell sx={thSx(true)}>
                    Monthly rental
                    <Ar sx={{ display: "block", fontWeight: 500, fontSize: "9.5px", opacity: 0.85, textAlign: "right" }}>
                      الإيجار الشهري
                    </Ar>
                  </TableCell>
                </>
              ) : isDn ? (
                <>
                  <TableCell sx={thSx(false)}>
                    Vehicle
                    <Ar sx={{ display: "block", fontWeight: 500, fontSize: "9.5px", opacity: 0.85 }}>
                      المركبة
                    </Ar>
                  </TableCell>
                  <TableCell sx={thSx(false)}>
                    Delivery note
                    <Ar sx={{ display: "block", fontWeight: 500, fontSize: "9.5px", opacity: 0.85 }}>
                      رقم مذكرة التسليم
                    </Ar>
                  </TableCell>
                </>
              ) : (
                <>
                  <TableCell sx={thSx(false)}>
                    Description
                    <Ar sx={{ display: "block", fontWeight: 500, fontSize: "9.5px", opacity: 0.85 }}>
                      الوصف
                    </Ar>
                  </TableCell>
                  <TableCell sx={thSx(true)}>
                    Qty
                    <Ar sx={{ display: "block", fontWeight: 500, fontSize: "9.5px", opacity: 0.85, textAlign: "right" }}>
                      الكمية
                    </Ar>
                  </TableCell>
                  <TableCell sx={thSx(true)}>
                    Unit price
                    <Ar sx={{ display: "block", fontWeight: 500, fontSize: "9.5px", opacity: 0.85, textAlign: "right" }}>
                      سعر الوحدة
                    </Ar>
                  </TableCell>
                </>
              )}
              {discCol && (
                <TableCell sx={thSx(true)}>
                  Discount
                  <Ar sx={{ display: "block", fontWeight: 500, fontSize: "9.5px", opacity: 0.85, textAlign: "right" }}>
                    الخصم
                  </Ar>
                </TableCell>
              )}
              <TableCell sx={thSx(true)}>
                Excl. VAT
                <Ar sx={{ display: "block", fontWeight: 500, fontSize: "9.5px", opacity: 0.85, textAlign: "right" }}>
                  الخاضع للضريبة
                </Ar>
              </TableCell>
              <TableCell sx={thSx(true)}>
                {`VAT ${rate}%`}
                <Ar sx={{ display: "block", fontWeight: 500, fontSize: "9.5px", opacity: 0.85, textAlign: "right" }}>
                  قيمة الضريبة
                </Ar>
              </TableCell>
              {showSav && (
                <TableCell sx={thSx(true)}>
                  Savings
                  <Ar sx={{ display: "block", fontWeight: 500, fontSize: "9.5px", opacity: 0.85, textAlign: "right" }}>
                    التوفير
                  </Ar>
                </TableCell>
              )}
              <TableCell sx={thSx(true)}>
                Total
                <Ar sx={{ display: "block", fontWeight: 500, fontSize: "9.5px", opacity: 0.85, textAlign: "right" }}>
                  الإجمالي
                </Ar>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(() => {
              let n = 0;
              return groups.map((g, gi) => (
                <React.Fragment key={gi}>
                  {(groups.length > 1 || g.alwaysTitle) && (
                    <TableRow>
                      <TableCell
                        colSpan={colCount}
                        sx={{
                          bgcolor: C.band,
                          py: "6px",
                          px: "7px",
                          borderBottom: 0,
                          borderTop: gi === 0 ? 0 : "6px solid #fff",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 1.25,
                            width: "100%",
                            minWidth: 0,
                          }}
                        >
                          <Box sx={{ minWidth: 0, overflow: "visible" }}>
                            <Typography
                              component="span"
                              sx={{
                                fontSize: 11,
                                color: C.navy,
                                fontWeight: 700,
                                letterSpacing: "0.02em",
                                textTransform: "uppercase",
                              }}
                            >
                              {g.en}
                            </Typography>
                            <Ar sx={{ fontSize: "10.5px", color: C.ink2, ml: 1 }}>
                              {g.ar}
                            </Ar>
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "10.5px",
                              color: C.muted,
                              whiteSpace: "nowrap",
                              flexShrink: 0,
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {g.rows.length}{" "}
                            {g.rows.length === 1 ? "line" : "lines"} ·{" "}
                            <Box
                              component="b"
                              sx={{ color: C.ink, fontVariantNumeric: "tabular-nums" }}
                            >
                              {money(g.sum)}
                            </Box>
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                  {g.rows.map((r, ri) => {
                    n += 1;
                    return (
                      <TableRow key={ri} sx={{ breakInside: "avoid" }}>
                        <TableCell sx={tdSx(false)}>{n}</TableCell>
                        {isLease ? (
                          <>
                            <TableCell sx={tdSx(false)}>
                              <Typography sx={{ fontWeight: 700, fontSize: "10.8px" }}>
                                {r.plate}
                              </Typography>
                              {has(r.plateAr) && (
                                <Ar sx={{ display: "block", color: C.muted, fontSize: "9.5px", textAlign: "left" }}>
                                  {r.plateAr}
                                </Ar>
                              )}
                            </TableCell>
                            <TableCell sx={tdSx(false)}>
                              <Typography sx={{ fontWeight: 700, fontSize: "10.8px" }}>
                                {r.vehicle}
                              </Typography>
                              <Typography sx={{ color: C.muted, fontSize: "9.5px", lineHeight: 1.3 }}>
                                {r.name}
                                {has(r.sub) ? ` · ${r.sub}` : ""}
                              </Typography>
                              {has(r.vin) && (
                                <Typography
                                  sx={{
                                    color: C.muted,
                                    fontSize: "9.5px",
                                    fontFamily: '"Roboto Mono",Consolas,monospace',
                                  }}
                                >
                                  VIN {r.vin}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={tdSx(true)}>{r.days ?? ""}</TableCell>
                            <TableCell sx={tdSx(true)}>{money(r.price)}</TableCell>
                          </>
                        ) : isDn ? (
                          <>
                            <TableCell sx={tdSx(false)}>
                              <Typography sx={{ fontWeight: 700, fontSize: "10.8px" }}>
                                {r.plate}
                              </Typography>
                              {(has(r.vehicle) || has(r.vin)) && (
                                <Typography sx={{ color: C.muted, fontSize: "9.5px", lineHeight: 1.3 }}>
                                  {r.vehicle || ""}
                                  {has(r.vin) ? ` · ${r.vin}` : ""}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={tdSx(false)}>
                              <Typography sx={{ fontWeight: 700, fontSize: "10.8px" }}>
                                {r.dn}
                              </Typography>
                              {has(r.date) && (
                                <Typography sx={{ color: C.muted, fontSize: "9.5px" }}>
                                  {r.date}
                                </Typography>
                              )}
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell sx={tdSx(false)}>
                              <Typography sx={{ fontWeight: 700, fontSize: "10.8px" }}>
                                {r.name}
                              </Typography>
                              {has(r.sub) && (
                                <Typography sx={{ color: C.muted, fontSize: "9.5px", lineHeight: 1.3 }}>
                                  {r.sub}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={tdSx(true)}>{r.qty}</TableCell>
                            <TableCell sx={tdSx(true)}>{money(r.price)}</TableCell>
                          </>
                        )}
                        {discCol && (
                          <TableCell
                            sx={{
                              ...tdSx(true),
                              color: r.disc > 0 ? C.ink : C.faint,
                            }}
                          >
                            {r.disc > 0 ? `−${money(r.disc)}` : "0.00"}
                          </TableCell>
                        )}
                        <TableCell sx={tdSx(true)}>{money(r.ex)}</TableCell>
                        <TableCell sx={tdSx(true)}>{money(r.v)}</TableCell>
                        {showSav && (
                          <TableCell
                            sx={{
                              ...tdSx(true),
                              color: r.sv > 0 ? C.greenDk : C.faint,
                            }}
                          >
                            {money(r.sv)}
                          </TableCell>
                        )}
                        <TableCell sx={tdSx(true)}>
                          <Box component="b">{money(r.tot)}</Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </React.Fragment>
              ));
            })()}
          </TableBody>
        </Table>

        {/* Totals + savings */}
        <Grid container spacing={2.5} sx={{ mt: 1.25, alignItems: "flex-start" }}>
          <Grid item xs={12} md={7}>
            {showSav && sav > 0 ? (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  bgcolor: "#e3f6ec",
                  color: C.greenDk,
                  fontWeight: 700,
                  fontSize: 11,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: "7px",
                }}
              >
                ✓ You saved <Dh />
                {money(sav)} on this {doc.docWord || "service"}
              </Box>
            ) : null}
            {doc.bank && (doc.bank.rows || []).some((r) => has(r[2])) && (
              <Box
                sx={{
                  border: `1px solid ${C.line}`,
                  borderRadius: "8px",
                  px: 1.5,
                  py: 1.25,
                  mt: showSav && sav > 0 ? 1.5 : 0,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography sx={{ fontSize: 12, color: C.navy, fontWeight: 700 }}>
                    Bank deposit and transfer details
                  </Typography>
                  <Ar sx={{ fontSize: "11.5px", color: C.ink2 }}>معلومات الدفع</Ar>
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr",
                    columnGap: 1.25,
                    rowGap: "1px",
                    fontSize: "10.5px",
                  }}
                >
                  {(doc.bank.rows || [])
                    .filter((r) => has(r[2]))
                    .map((r, i) => (
                      <React.Fragment key={i}>
                        <Typography sx={{ color: C.muted, fontSize: "10.5px" }}>{r[0]}</Typography>
                        <Typography
                          sx={{
                            fontSize: "10.5px",
                            fontFamily: /IBAN|Account no/i.test(r[0])
                              ? '"Roboto Mono",Consolas,monospace'
                              : "inherit",
                          }}
                        >
                          <Ltr>{r[2]}</Ltr>
                        </Typography>
                      </React.Fragment>
                    ))}
                </Box>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={5}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {discCol && (
                <>
                  <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.375, fontSize: "11.5px" }}>
                    <span>
                      Gross amount
                      <Ar sx={{ fontSize: 10, color: C.faint, ml: 0.6 }}>المبلغ قبل الخصم</Ar>
                    </span>
                    <b style={{ fontVariantNumeric: "tabular-nums" }}>{money(gross)}</b>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.375, fontSize: "11.5px" }}>
                    <span>
                      Discount
                      <Ar sx={{ fontSize: 10, color: C.faint, ml: 0.6 }}>الخصم</Ar>
                    </span>
                    <b style={{ fontVariantNumeric: "tabular-nums" }}>−{money(dsc)}</b>
                  </Box>
                </>
              )}
              <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.375, fontSize: "11.5px" }}>
                <span>
                  {(doc.totalLabels?.sub && doc.totalLabels.sub[0]) ||
                    "Subtotal (excl. VAT)"}
                  <Ar sx={{ fontSize: 10, color: C.faint, ml: 0.6 }}>
                    {(doc.totalLabels?.sub && doc.totalLabels.sub[1]) ||
                      "المجموع الفرعي"}
                  </Ar>
                </span>
                <b style={{ fontVariantNumeric: "tabular-nums" }}>{money(net)}</b>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.375, fontSize: "11.5px" }}>
                <span>
                  {(doc.totalLabels?.vat && doc.totalLabels.vat[0]) ||
                    `VAT ${rate}%`}
                  <Ar sx={{ fontSize: 10, color: C.faint, ml: 0.6 }}>
                    {(doc.totalLabels?.vat && doc.totalLabels.vat[1]) ||
                      "قيمة الضريبة"}
                  </Ar>
                </span>
                <b style={{ fontVariantNumeric: "tabular-nums" }}>{money(vat)}</b>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  borderTop: `1px solid ${C.line}`,
                  mt: 0.5,
                  pt: 1.125,
                  fontSize: 14,
                  color: C.greenDk,
                  fontWeight: 700,
                }}
              >
                <span>
                  {(doc.totalLabels?.total && doc.totalLabels.total[0]) ||
                    "Total amount"}
                  <Ar sx={{ fontSize: 10, color: C.faint, ml: 0.6, fontWeight: 400 }}>
                    {(doc.totalLabels?.total && doc.totalLabels.total[1]) ||
                      "المبلغ الإجمالي"}
                  </Ar>
                </span>
                <b style={{ fontSize: 15, fontVariantNumeric: "tabular-nums" }}>
                  <Dh />
                  {money(total)}
                </b>
              </Box>
              {showSav && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 0.375,
                    fontSize: "11.5px",
                    color: sav > 0 ? C.greenDk : C.faint,
                    fontWeight: 700,
                  }}
                >
                  <span>
                    Total savings
                    <Ar sx={{ fontSize: 10, ml: 0.6, fontWeight: 400 }}>إجمالي التوفير</Ar>
                  </span>
                  <b style={{ fontVariantNumeric: "tabular-nums" }}>{money(sav)}</b>
                </Box>
              )}
              {doc.paid != null && (
                <>
                  <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.375, fontSize: "11.5px" }}>
                    <span>
                      Paid
                      <Ar sx={{ fontSize: 10, color: C.faint, ml: 0.6 }}>المدفوع</Ar>
                    </span>
                    <b style={{ fontVariantNumeric: "tabular-nums" }}>{money(doc.paid)}</b>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      py: 0.375,
                      fontSize: "11.5px",
                      color: "#a65f00",
                      fontWeight: 700,
                    }}
                  >
                    <span>
                      Balance due
                      <Ar sx={{ fontSize: 10, ml: 0.6, fontWeight: 400 }}>الرصيد المستحق</Ar>
                    </span>
                    <b style={{ fontVariantNumeric: "tabular-nums" }}>
                      <Dh />
                      {money(doc?.invoiceBalance?doc?.invoiceBalance:total - (+doc.paid || 0))}
                    </b>
                  </Box>
                </>
              )}
            </Box>
          </Grid>
        </Grid>
          </>
        )}

        {/* Thanks (customer receiving) */}
        {has(doc.thanks?.en) && (
          <Box
            sx={{
              mt: 2.25,
              textAlign: "center",
              fontWeight: 700,
              color: C.navy,
              fontSize: "12.5px",
            }}
          >
            {doc.thanks.en}
            {has(doc.thanks.ar) && (
              <Ar sx={{ ml: 0.75 }}>{doc.thanks.ar}</Ar>
            )}
          </Box>
        )}

        {/* Terms */}
        {doc.terms && doc.terms.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ m: 0, mb: 0.75, fontSize: 13, color: C.navy, fontWeight: 700 }}>
              Terms and conditions /{" "}
              <Ar sx={{ fontSize: 12 }}>الشروط والأحكام</Ar>
            </Typography>
            <Box
              component="ol"
              sx={{
                m: 0,
                pl: 2,
                fontSize: "10.5px",
                color: C.ink2,
                lineHeight: 1.55,
              }}
            >
              {doc.terms.map((t, i) => (
                <Box component="li" key={i} sx={{ mb: 0.375, breakInside: "avoid" }}>
                  {t.en}
                  {has(t.ar) && (
                    <Ar sx={{ display: "block", color: C.muted, textAlign: "right" }}>{t.ar}</Ar>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Footer */}
        <Box
          sx={{
            mt: 1.5,
            pt: 0.75,
            borderTop: `1px solid ${C.line}`,
            display: "flex",
            justifyContent: "space-between",
            gap: 1.25,
            fontSize: "9.5px",
            color: C.faint,
          }}
        >
          <span>
            {doc.footer?.en || ""}
            {doc.footer?.ar ? (
              <>
                {" / "}
                <Ar>{doc.footer.ar}</Ar>
              </>
            ) : null}
          </span>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>
            Printed {doc.printed || ""}
          </span>
        </Box>
      </Box>
    </Box>
  );
}
